import { TEST_IDS } from '@argus-labs/auth-shared/test-ids'
import type { ReactNode } from 'react'
import { toast as sonnerToast } from 'sonner'
import { AlertIcon } from '@/components/decorative/alert-icon'
import { XIcon } from '@/components/decorative/x-icon'
import { Text } from '@/components/ui/text'

interface ToastProps {
  id: string | number
  description: ReactNode
  errorCode?: string
}

/**
 * Customize Sonner toast to be our own
 */
function toast(toast: Omit<ToastProps, 'id'> & { id?: string }) {
  return sonnerToast.custom(
    (id) => <Toast id={id} description={toast.description} errorCode={toast.errorCode} />,
    // A fixed id replaces an earlier toast with the same id instead of stacking a duplicate.
    { id: toast.id, testId: TEST_IDS.toast },
  )
}

/**
 * Our toast that accepts `error` object from Better Auth API calls
 */
toast.fromError = (
  error: {
    code?: string | undefined
    message?: string | undefined
    status: number
    statusText: string
  } | null,
) => {
  if (!error) return
  return toast({
    description:
      error.status === 429
        ? 'Too many attempts. Try again later.'
        : (error.message ?? 'Something went wrong. Try again.'),
    errorCode: error.code,
  })
}

toast.dismiss = (id: string) => sonnerToast.dismiss(id)

const PRESETS = {
  NETWORK_ERROR: {
    description: 'Couldn’t connect. Check your connection and try again.',
    errorCode: 'NETWORK_ERROR',
  },
} as const satisfies Record<string, { description: string; errorCode?: string }>

/**
 * Our toast with preset description and error code
 */
toast.fromPreset = (preset: keyof typeof PRESETS) => {
  return toast({
    description: PRESETS[preset].description,
    errorCode: PRESETS[preset].errorCode,
  })
}

export { toast }

function Toast(props: ToastProps) {
  const { description, errorCode, id } = props

  return (
    <div className="px-4 md:w-96">
      <div className="flex items-start gap-3 rounded-lg border border-border bg-card py-3 pl-4">
        <AlertIcon className="mt-px size-5 shrink-0 text-destructive" />

        <div className="flex flex-1 flex-col gap-1">
          <Text font="beausite" size={14} color="cream" block>
            {description}
          </Text>
          {errorCode ? (
            <Text font="beausite" size={12} block className="text-muted-foreground">
              Error code: {errorCode}
            </Text>
          ) : null}
        </div>

        {/* The pseudo-element keeps a 44px target around the small icon. */}
        <button
          type="button"
          aria-label="Dismiss"
          className="relative mr-2 shrink-0 cursor-pointer p-1 text-muted-foreground hover:text-cream after:absolute after:-inset-2.5"
          onClick={() => {
            sonnerToast.dismiss(id)
          }}
        >
          <XIcon className="size-4" />
        </button>
      </div>
    </div>
  )
}
